import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Stack, Typography } from "@mui/material";
import CustomButton from "./CustomButton";

const SIZE = 200;
const MAX_FILE_SIZE = SIZE * 1024 * 1024; // 20MB in bytes
function FileUploadBoard({
  subTitle = `Files up to ${SIZE}MB (PDFs and images)`,
  onAddFiles,
  acceptedFiles = ["*"], // Default: Accept all file types
}) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      onAddFiles(acceptedFiles);
      const validFiles = [];
      const invalidFiles = [];

      acceptedFiles.forEach((file) => {
        if (file.size <= MAX_FILE_SIZE) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        alert(
          `These files exceed ${SIZE}MB and were not added: ${invalidFiles.join(
            ", "
          )}`
        );
      }

      const resultsFiles = [];
      validFiles.forEach((file) => {
        const reader = new FileReader();

        reader.onabort = () => console.log("File reading was aborted");
        reader.onerror = () => console.log("File reading has failed");
        reader.onload = () => {
          const binaryStr = reader.result;

          const newFile = { file: file, b64: binaryStr };
          resultsFiles.push(newFile);
        };
        reader.readAsDataURL(file);
      });

      onAddFiles(resultsFiles);
    },
    [onAddFiles]
  );

  // Generate the accept prop for useDropzone
  const accept = acceptedFiles.reduce((acc, fileType) => {
    switch (fileType.toLowerCase()) {
      case "pdf":
        acc["application/pdf"] = [];
        break;
      case "image":
        acc["image/jpeg"] = [];
        acc["image/png"] = [];
        acc["image/gif"] = [];
        break;
      case "word":
        acc["application/msword"] = []; // For .doc
        acc[
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ] = []; // For .docx
        break;
      case "spreadsheet":
        acc["application/vnd.ms-excel"] = []; // For .xls
        acc[
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ] = []; // For .xlsx
        break;
      default:
        break;
    }
    return acc;
  }, {});

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: Object.keys(accept).length ? accept : undefined,
    maxSize: MAX_FILE_SIZE, // Directly enforce file size in dropzone
  });

  return (
    <Box
      sx={{
        width: "100%",
        height: "193px",
        border: "2px dashed #CBD0DC",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack sx={{ p: 1 }} direction={"column"} spacing={1} {...getRootProps()}>
        <input {...getInputProps()} />
        <Stack spacing={4} alignItems={"center"}>
          <hgroup>
            <Typography sx={{ fontWeight: "700", fontSize: "12px" }}>
              Choose a file or drag & drop it here
            </Typography>
            <Typography
              sx={{ opacity: 0.7, fontSize: "14px", textAlign: "center" }}
            >
              {subTitle}
            </Typography>
          </hgroup>
          {isDragActive ? (
            <Typography>Drop the files here ...</Typography>
          ) : (
            <CustomButton
              sx={{
                fontSize: "14px",
                color: "#2D5BFF",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#ECF0FF",
                width: "168px",
              }}
              variant={"outlined"}
              text={"Browse files"}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export default FileUploadBoard;
